'use client';

import { Link } from 'react-router-dom';
import { V2Layout, RevealSection, useV2Lang, Eyebrow, Rule, ACCENT, HAIRLINE, PageHeader, DisplayTitle } from '../_layout';

/**
 * /v2/about — consolidated About page.
 * Combines: mission/manifesto · history · team · operating principles.
 * Replaces the redundant nav slot for "manifesto anchor" and the empty "team anchor".
 */

const COPY = {
  CN: {
    kicker: '关于 PathIN',
    title: 'PathIN 是一座桥。',
    lede: '注册于慕尼黑的非营利协会 (gemeinnütziger eingetragener Verein)，通过独立音乐与诗歌让中欧的文化对话变得可执行、可重复、可被记住。',
    sections: {
      mission: '01 · 使命',
      history: '02 · 历程',
      team: '03 · 团队',
      principles: '04 · 工作方式',
    },
    mission: {
      headline: (
        <>
          我们让中国音乐人登上欧洲的舞台,让欧洲诗人走进中文读者的视野。让<em className="not-italic" style={{ color: ACCENT }}>远东</em>不再遥远——又不抹平它的棱角。
        </>
      ),
      body: (
        <>
          PathIN 于 2025 年 4 月在慕尼黑注册成立,下设两条长期线。
          <span className="text-white"> GraviLens（引力透镜）</span>负责音乐板块——巡演规划、厂牌与场地合作、发行、跨城统筹;
          <span className="text-white"> Qiaodong（桥洞）</span> 负责翻译与诗歌——工作坊、节展策划、双语选集。一体两面。
        </>
      ),
    },
    history: [
      { date: '2024', label: '萌芽', body: '从一个微信群和几次柏林饭局开始——讨论"中国独立音乐怎么才能在欧洲落地不止一夜"。' },
      { date: '2025 · 4 月', label: '注册成立', body: 'PathIN e.V. 在慕尼黑注册为公益协会 (gemeinnütziger eingetragener Verein)，首届理事会就位。' },
      { date: '2025 · 5 月', label: 'FAZI 欧洲巡演', body: '把西安后朋克 FAZI 带到柏林 Maschinenhaus + 科隆 Blue Shell。GraviLens 第一次全程自营的多城联动。' },
      { date: '2025 · 6 月', label: 'An Corporation 联合演出', body: '上海器乐摇滚 An Corporation 受 Nugget Records 邀请落地柏林 ACUD MACHT NEU,与本地 Dusk Saffron、gossamer sea 完成"东方后摇 × 欧洲 shoegaze"的对话。当晚 ACUD 售罄。' },
      { date: '2025 · 6 月', label: '柏林诗歌节中国专场', body: '受 Haus für Poesie 邀请,桥洞策划"朦胧的诗与具体的人",把九零后中国诗人带到 Akademie der Künste 主舞台。' },
      { date: '2026 · 3 月', label: '万青柏林站', body: '万能青年旅店 2026 欧洲巡演德国站——中国独立音乐殿堂级乐队首度赴欧,德国只有柏林这一晚。Myna Stage × GraviLens 联合呈现,开票当日售罄。' },
      { date: '2026 · 5 月', label: '时过夏末欧洲巡演', body: '5 站欧洲巡演,并受邀参加比利时 Dunk! Festival。GraviLens 第一次"全产业链"运营。' },
    ],
    team: {
      lede: '13 个人，5 个职能组。',
    },
    principles: [
      {
        n: '01',
        title: '不为单场演出',
        body: '我们不是 booking agency。每个项目都看一年、五年——能不能让下一支乐队、下一位诗人接力。',
      },
      {
        n: '02',
        title: '内容先于流量',
        body: '宁可慢、宁可少——但每一次推送、每一篇翻译、每一份手册都要经得起被存到 Pocket 里。',
      },
      {
        n: '03',
        title: '透明结算',
        body: '每个项目结束后公开收支与产出,优先发给捐赠人和合作方。公益注册带来的不是免税,是问责。',
      },
      {
        n: '04',
        title: '尊重原产地',
        body: '我们不试图把"中国"翻译成欧洲容易消化的版本。让远东不再遥远——又不抹平它的棱角。',
      },
    ],
    cta: { kicker: '一起做点什么', body: '艺人邀约、媒体咨询、巡演合作、捐赠、加入我们——欢迎写信。', cta: '写信给 PathIN →' },
  },
  EN: {
    kicker: 'About PathIN',
    title: 'PathIN is a bridge.',
    lede: 'A Munich-registered nonprofit (gemeinnütziger eingetragener Verein) making cultural dialogue between China and Europe operational, repeatable and memorable — through independent music and poetry.',
    sections: {
      mission: '01 · Mission',
      history: '02 · Timeline',
      team: '03 · Team',
      principles: '04 · How we work',
    },
    mission: {
      headline: (
        <>
          We send Chinese musicians to European stages. We bring European poets into Chinese readers&apos; hands. We make the <em className="not-italic" style={{ color: ACCENT }}>far East</em> feel less far — without smoothing it down.
        </>
      ),
      body: (
        <>
          Founded in Munich in April 2025 as a registered nonprofit, PathIN runs two long-form programs.
          <span className="text-white"> GraviLens（引力透镜）</span> handles music — tour planning, label and venue partnerships, releases, multi-city logistics.
          <span className="text-white"> Qiaodong（桥洞）</span> handles translation and poetry — workshops, festival programming, anthologies. One mission, two angles.
        </>
      ),
    },
    history: [
      { date: '2024', label: 'Origins', body: 'Started in a WeChat group and a few dinner conversations in Berlin — asking how Chinese independent music could land in Europe for more than one night.' },
      { date: 'April 2025', label: 'Registered', body: 'PathIN e.V. registered as gemeinnütziger eingetragener Verein in Munich. First Vorstand seated.' },
      { date: 'May 2025', label: 'FAZI European tour', body: "Brought Xi'an post-punk band FAZI to Maschinenhaus Berlin + Blue Shell Köln. GraviLens's first end-to-end multi-city operation." },
      { date: 'June 2025', label: 'An Corporation joint show', body: "Shanghai instrumental rock band An Corporation landed at ACUD MACHT NEU on Nugget Records' invitation, joined by Berlin-based Dusk Saffron and gossamer sea — a full evening of Eastern post-rock × European shoegaze. Sold out at ACUD." },
      { date: 'June 2025', label: 'Berlin Poetry Festival · China program', body: 'Invited by Haus für Poesie, Qiaodong programmed the main-stage primetime slot — bringing 90s-born Chinese poets to Akademie der Künste.' },
      { date: 'March 2026', label: 'Omnipotent Youth Society · Berlin', body: "OYS's first European tour — Berlin was the only German date. Co-presented by Myna Stage and GraviLens. Sold out the day tickets opened." },
      { date: 'May 2026', label: 'Summer Fades Away · European Tour', body: '5-city European tour plus invited slot at Dunk! Festival, Belgium. GraviLens\'s first full-stack operation.' },
    ],
    team: {
      lede: '13 people, five functional teams.',
    },
    principles: [
      { n: '01', title: 'Not booking-by-booking', body: "We're not a booking agency. Every project is sized in years, not nights — does it pave the way for the next band, the next poet?" },
      { n: '02', title: 'Content before reach', body: 'We post less, slower — but every newsletter, translation and handbook should be worth saving to Pocket.' },
      { n: '03', title: 'Transparent ledgers', body: "After every project we publish budget and outputs first to donors and partners. Nonprofit status isn't tax relief — it's accountability." },
      { n: '04', title: 'Respect for origin', body: "We don't try to translate 'China' into a version Europe finds easy to digest. Make the far East less far — without smoothing it down." },
    ],
    cta: { kicker: 'Work with us', body: 'Artist booking, press inquiries, tour partnerships, donations, joining us — write to PathIN.', cta: 'Email PathIN →' },
  },
  DE: {
    kicker: 'Über PathIN',
    title: 'PathIN ist eine Brücke.',
    lede: 'Ein in München eingetragener gemeinnütziger Verein, der den Kulturdialog zwischen China und Europa operativ, wiederholbar und einprägsam macht — durch unabhängige Musik und Lyrik.',
    sections: {
      mission: '01 · Mission',
      history: '02 · Chronik',
      team: '03 · Team',
      principles: '04 · Arbeitsweise',
    },
    mission: {
      headline: (
        <>
          Wir bringen chinesische Musiker:innen auf europäische Bühnen. Wir bringen europäische Lyrik in chinesische Hände. Wir lassen den <em className="not-italic" style={{ color: ACCENT }}>Fernen Osten</em> näher rücken — ohne ihn zu glätten.
        </>
      ),
      body: (
        <>
          Im April 2025 in München als gemeinnütziger eingetragener Verein gegründet, betreibt PathIN zwei langfristige Programme.
          <span className="text-white"> GraviLens（引力透镜）</span> kümmert sich um Musik — Tourneeplanung, Label- und Venue-Partnerschaften, Releases, Logistik über mehrere Städte.
          <span className="text-white"> Qiaodong（桥洞）</span> kümmert sich um Übersetzung und Poesie — Workshops, Festival-Programmierung, Anthologien. Eine Mission, zwei Perspektiven.
        </>
      ),
    },
    history: [
      { date: '2024', label: 'Anfänge', body: 'Begann in einer WeChat-Gruppe und ein paar Berliner Abendessen — mit der Frage, wie chinesische Independent-Musik in Europa länger als eine Nacht landen kann.' },
      { date: 'April 2025', label: 'Registrierung', body: 'PathIN e.V. als gemeinnütziger eingetragener Verein in München eingetragen. Erster Vorstand gewählt.' },
      { date: 'Mai 2025', label: 'FAZI Europatournee', body: 'Die Xi\'aner Post-Punk-Band FAZI im Maschinenhaus Berlin + Blue Shell Köln. GraviLens\' erste komplett selbst gestaltete Mehr-Städte-Tour.' },
      { date: 'Juni 2025', label: 'An Corporation Gemeinschaftskonzert', body: 'Die Shanghaier Instrumental-Rock-Band An Corporation gastierte auf Einladung von Nugget Records im Berliner ACUD MACHT NEU, gemeinsam mit Dusk Saffron und gossamer sea — ein Abend zwischen östlichem Post-Rock und europäischem Shoegaze. Ausverkauft.' },
      { date: 'Juni 2025', label: 'Poesiefestival Berlin · China-Programm', body: 'Auf Einladung des Hauses für Poesie kuratierte Qiaodong den Hauptbühnen-Primetime-Slot — chinesische Lyriker:innen der 90er-Generation in der Akademie der Künste.' },
      { date: 'März 2026', label: 'Omnipotent Youth Society · Berlin', body: 'Die erste Europatournee von OYS — Berlin war der einzige Termin in Deutschland. Co-präsentiert von Myna Stage und GraviLens. Am Verkaufstag ausverkauft.' },
      { date: 'Mai 2026', label: 'Summer Fades Away Europatournee', body: '5-Städte-Tour plus Einladung zum Dunk! Festival in Belgien. GraviLens\' erste vollständige Eigenproduktion.' },
    ],
    team: {
      lede: '13 Menschen, fünf Funktionsteams.',
    },
    principles: [
      { n: '01', title: 'Keine Konzert-zu-Konzert-Logik', body: 'Wir sind keine Booking-Agentur. Jedes Projekt denken wir in Jahren — kann es den Weg für die nächste Band, die nächste Lyriker:in ebnen?' },
      { n: '02', title: 'Inhalt vor Reichweite', body: 'Wir posten weniger und langsamer — aber jeder Newsletter, jede Übersetzung, jedes Handbuch soll es wert sein, abgespeichert zu werden.' },
      { n: '03', title: 'Transparente Bilanz', body: 'Nach jedem Projekt veröffentlichen wir Budget und Ergebnis — zuerst an Spender:innen und Partner. Gemeinnützigkeit ist kein Steuervorteil, sondern Rechenschaft.' },
      { n: '04', title: 'Respekt für die Herkunft', body: '"China" wird hier nicht in eine für Europa leichter verdauliche Version übersetzt. Den Fernen Osten näher rücken — ohne ihn zu glätten.' },
    ],
    cta: { kicker: 'Zusammenarbeit', body: 'Booking, Presse, Tour-Kooperationen, Spenden, Mitarbeit — schreiben Sie uns.', cta: 'PathIN schreiben →' },
  },
};

type TeamMember = { name: string; role?: { CN: string; EN: string; DE: string } };
type TeamGroup = { name: { CN: string; EN: string; DE: string }; members: TeamMember[] };

const TEAMS: TeamGroup[] = [
  {
    name: { CN: '理事会 Vorstand', EN: 'Vorstand · Board', DE: 'Vorstand' },
    members: [
      { name: '黄铭 · Ming Huang', role: { CN: '会长', EN: 'President', DE: 'Vorsitzender' } },
      { name: '何柏岚 · Bailan He', role: { CN: '副会长', EN: 'Vice President', DE: 'Vize-Vorsitzender' } },
      { name: '马可 · Ke Ma', role: { CN: '秘书 · 财务', EN: 'Secretary · Finance', DE: 'Schriftführer · Finanzen' } },
    ],
  },
  {
    name: { CN: 'GraviLens 引力透镜', EN: 'GraviLens 引力透镜', DE: 'GraviLens 引力透镜' },
    members: [
      { name: '韩冰意 · Bingyi Han', role: { CN: '艺人关系', EN: 'Artist Relations', DE: 'Künstler:innen-Relations' } },
      { name: '杨润荃 · David Runquan Yang', role: { CN: '巡演资源', EN: 'Tour Resources', DE: 'Tour-Ressourcen' } },
      { name: '谢沛文 · Peiwen Xie', role: { CN: '场地 · 现场', EN: 'Venues · Production', DE: 'Venues · Produktion' } },
      { name: '高睿 · Tony Rui Gao', role: { CN: '现场 · 巡演执行', EN: 'Production · Tour Manager', DE: 'Produktion · Tour-Management' } },
      { name: '张子琼 · Joe Ziqiong Zhang', role: { CN: '财务 · 票务 · 市场', EN: 'Finance · Ticketing · Marketing', DE: 'Finanzen · Ticketing · Marketing' } },
    ],
  },
  {
    name: { CN: 'Qiaodong 桥洞', EN: 'Qiaodong 桥洞', DE: 'Qiaodong 桥洞' },
    members: [
      { name: '孙汤明米 · Tangmi', role: { CN: '项目主理', EN: 'Program Lead', DE: 'Programmleitung' } },
    ],
  },
  {
    name: { CN: '内容与传播', EN: 'Content & Comms', DE: 'Content & Kommunikation' },
    members: [
      { name: '芮雨田 · Yutian Rui', role: { CN: '视觉 · 后期 · 中文社媒', EN: 'Visual · Post · CN Social', DE: 'Visuell · Post · CN-Social' } },
      { name: '陈希 · Xi Chen', role: { CN: '海外社媒', EN: 'International Social', DE: 'Internationale Social-Media' } },
      { name: '杜瑞珈 · Ruijia Du', role: { CN: '网站开发', EN: 'Web Development', DE: 'Web-Entwicklung' } },
    ],
  },
  {
    name: { CN: '顾问 · 地区联络', EN: 'Advisors · Regional Liaison', DE: 'Beirat · Regionale Verbindung' },
    members: [
      { name: '叶弎 · San Ye', role: { CN: '柏林地区联系人', EN: 'Berlin Regional Contact', DE: 'Berliner Regionalkontakt' } },
    ],
  },
];

export default function V2About() {
  const lang = useV2Lang();
  const t = COPY[lang];

  return (
    <V2Layout hideFooterContact>
      <PageHeader kicker={t.kicker} title={<DisplayTitle text={t.title} />} lede={t.lede} />

      <Rule />

      {/* MISSION */}
      <RevealSection id="mission" className="px-6 md:px-10 py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] grid gap-12 md:grid-cols-12">
          <div className="md:col-span-3"><Eyebrow>{t.sections.mission}</Eyebrow></div>
          <div className="md:col-span-8">
            <p className="v2-cjk-tight text-[clamp(28px,3.2vw,48px)] leading-[1.22] tracking-[-0.025em] font-medium text-white">{t.mission.headline}</p>
            <p className="mt-10 text-[16px] leading-[1.75] text-white/80 max-w-[64ch]">{t.mission.body}</p>
          </div>
        </div>
      </RevealSection>

      <Rule />

      {/* HISTORY */}
      <RevealSection id="history" className="px-6 md:px-10 py-24 md:py-32">
        <div className="mx-auto max-w-[1400px]">
          <div className="grid gap-12 md:grid-cols-12 mb-16">
            <div className="md:col-span-3"><Eyebrow>{t.sections.history}</Eyebrow></div>
          </div>
          <div className="border-t" style={{ borderColor: HAIRLINE }}>
            {t.history.map((item, i) => (
              <div
                key={i}
                className="grid grid-cols-12 gap-x-6 gap-y-2 py-8 md:py-10 border-b items-start"
                style={{ borderColor: HAIRLINE }}
              >
                <span className="col-span-12 md:col-span-2 font-mono text-[12px] uppercase tracking-[0.15em] text-white/65 mt-1.5 font-medium">
                  {item.date}
                </span>
                <div className="col-span-12 md:col-span-9 md:col-start-3">
                  <h3 className="v2-cjk-tight text-[clamp(20px,1.8vw,28px)] leading-[1.25] tracking-[-0.015em] font-semibold text-white">
                    {item.label}
                  </h3>
                  <p className="mt-3 text-[15px] leading-[1.7] text-white/80 max-w-[60ch]">{item.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </RevealSection>

      <Rule />

      {/* TEAM */}
      <RevealSection id="team" className="px-6 md:px-10 py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] grid gap-12 md:grid-cols-12">
          <div className="md:col-span-3">
            <Eyebrow>{t.sections.team}</Eyebrow>
            <p className="mt-4 text-[13px] leading-[1.55] text-white/65 max-w-[28ch]">{t.team.lede}</p>
          </div>
          <div className="md:col-span-9">
            {TEAMS.map((team) => (
              <div key={team.name.EN} className="mb-12 last:mb-0">
                <h3 className="font-mono text-[11px] tracking-[0.2em] uppercase text-white/55 font-medium pb-3 border-b" style={{ borderColor: HAIRLINE }}>
                  {team.name[lang]}
                </h3>
                <ul>
                  {team.members.map((m) => (
                    <li key={m.name} className="grid grid-cols-12 gap-4 border-b py-5 items-baseline" style={{ borderColor: HAIRLINE }}>
                      <span className="col-span-12 md:col-span-7 text-[clamp(18px,1.5vw,22px)] tracking-tight text-white">{m.name}</span>
                      {m.role && (
                        <span className="col-span-12 md:col-span-5 text-[13px] text-white/70">{m.role[lang]}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </RevealSection>

      <Rule />

      {/* PRINCIPLES */}
      <RevealSection id="principles" className="px-6 md:px-10 py-24 md:py-32">
        <div className="mx-auto max-w-[1400px]">
          <div className="grid gap-12 md:grid-cols-12 mb-12">
            <div className="md:col-span-3"><Eyebrow>{t.sections.principles}</Eyebrow></div>
          </div>
          <div className="grid gap-x-12 md:grid-cols-2 border-t" style={{ borderColor: HAIRLINE }}>
            {t.principles.map((p) => (
              <div key={p.n} className="border-b py-10 md:py-12 md:pr-8" style={{ borderColor: HAIRLINE }}>
                <span className="font-mono text-[12px] tracking-[0.18em] uppercase font-semibold" style={{ color: ACCENT }}>{p.n}</span>
                <h3 className="v2-cjk-tight mt-3 text-[clamp(22px,2vw,30px)] leading-[1.25] tracking-[-0.02em] font-bold">{p.title}</h3>
                <p className="mt-4 text-[15px] leading-[1.7] text-white/80 max-w-[55ch]">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </RevealSection>

      <Rule />

      {/* CTA */}
      <RevealSection className="px-6 md:px-10 py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] grid gap-12 md:grid-cols-12">
          <div className="md:col-span-3"><Eyebrow accent>{t.cta.kicker}</Eyebrow></div>
          <div className="md:col-span-9">
            <p className="v2-cjk-tight text-[clamp(20px,2vw,28px)] leading-[1.45] text-white/90 max-w-[55ch]">{t.cta.body}</p>
            <Link
              to="/kontakt"
              className="inline-block mt-8 px-6 py-3 border text-[14px] tracking-wide hover:bg-white hover:text-black transition-colors"
              style={{ borderColor: 'rgba(255,255,255,0.4)' }}
            >
              {t.cta.cta}
            </Link>
          </div>
        </div>
      </RevealSection>

      <Rule />
    </V2Layout>
  );
}
